import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartRow,
  type ChartBaseProps,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type EngagementByTypeView = {
  /** `Most valuable type` and `Leading channel`. */
  kpis: KPIMetric[];
  /** One row per engagement type — Shares, Comments, Reactions — most valuable first. */
  rows: ChartRow[];
  xKey: string;
  /** One stack segment per channel present in scope. */
  series: ChartSeries[];
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and series; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type EngagementByTypeCardProps = WidgetBlockProps & {
  view?: EngagementByTypeView;
  /** The header and its help. Default `Engagement volume by type`; the executive `Engagement breakdown`. */
  title?: string;
  helpText?: string;
};

/**
 * Figma `849:29599` — each engagement type stacked by channel.
 *
 * **Three types, not four.** `Likes` was removed on 2026-09-23 and folded into
 * `Reactions`: a like is a reaction, and the two bars measured the same
 * gesture. The three left sum to `engagement` exactly, so the stack is the
 * whole of it.
 *
 * No drill-down, as in the prototype: every bar is reduced over the same rows,
 * so there is no "the conversations behind Comments" to open.
 */
export function EngagementByTypeCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  title = 'Engagement volume by type',
  helpText = 'Engagement totals by interaction type, split across channels. Shows which interactions and platforms contribute to engagement.',
}: EngagementByTypeCardProps) {
  /* No series means no rows in scope — the channels are the ones present. */
  const hasData = view !== undefined && view.series.length > 0 && !isEmpty(view.rows, view.series);
  return (
    <Card
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title={title}
      helpText={helpText}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            xKey={view.xKey}
            series={view.series}
            stacked
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      /* Split by channel — Figma's `Orientation=Platforms` legend. */
      legend={view && <ChartLegend items={legendItems(view.series)} orientation="platforms" />}
    />
  );
}
